import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Modal,
  Pressable,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle } from 'react-native-svg';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPct } from '../../utils/formatPercentage';
import { commitmentColor } from '../../utils/metricColors';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, EvidenceTable } from '../../components';
import { ExportMenuButton } from '../../components/ExportMenuButton';
import { useCanViewAllOrgs } from '../../components/OrganizationFilterDropdown';
import { DropdownPortal, useDropdownAnchor } from '../../components/DropdownPortal';
import { GoalsSkeleton } from '../../components/Skeleton';
import PendingDeletionBadge from '../../components/PendingDeletionBadge';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { unitGoalsApi, organizationalUnitsApi } from '../../services/apiService';
import { downloadBlobAsFile } from '../../utils/downloadFile';
import { useUnitGoalsQuery } from '../../hooks/queries';
import { queryKeys } from '../../providers/QueryProvider';
import { UnitGoalList, OrganizationalUnitList } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { PageHeader } from '@design/components/PageHeader';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icons
// ============================================================
const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronRightIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FileInputIcon = ({ size = 13, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7L15 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 15H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 13L10 15L8 17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);


const MoreVerticalIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="5" r="1" fill={color} />
    <Circle cx="12" cy="12" r="1" fill={color} />
    <Circle cx="12" cy="19" r="1" fill={color} />
  </Svg>
);

const EditIcon = ({ size = 16, color = tokens.colors.raw.gray["700"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const XIcon = ({ size = 16, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Circular Progress Component
// ============================================================
interface CircularProgressProps {
  percentage: number | null | undefined;
  size?: number;
  strokeWidth?: number;
  trackColor?: string;
  textColor?: string;
}

/**
 * Get color based on achievement percentage
 * - >= 100%: Green (#22C55E) - ممتاز
 * - >= 80% and < 100%: Orange (#F97316) - جيد
 * - < 80%: Red (#EF4444) - يحتاج تحسين
 */
const getAchievementColor = (percentage: number): string => {
  if (percentage >= 100) return tokens.colors.raw.green["500"];  // Green - Excellent
  if (percentage >= 80) return tokens.colors.raw.orange["500"];   // Orange - Good
  return tokens.colors.raw.red["500"];                          // Red - Needs Improvement
};

const CircularProgress = ({ percentage, size = 80, strokeWidth = 6, trackColor = tokens.colors.raw.gray["200"], textColor = tokens.colors.semantic.general.foreground }: CircularProgressProps) => {
  // Commitment rate is null when no measurement period is due yet → show "--"
  if (percentage == null) {
    return (
      <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={[styles.progressText, { color: tokens.colors.raw.gray["400"] }]}>--</Text>
      </View>
    );
  }
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(100, Math.max(0, percentage));
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const center = size / 2;

  // Unified commitment color scale (>=80 green / >=50 orange / <50 red)
  const progressColor = commitmentColor(percentage);
  const bgColor = trackColor;

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((progress / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={bgColor}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {progress > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={progressColor}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View style={styles.progressTextContainer}>
        <Text style={[styles.progressText, { color: textColor }]}>%{formatPct(percentage)}</Text>
      </View>
    </View>
  );
};

// ============================================================
// Goal Card Component
// ============================================================
interface GoalCardProps {
  goal: UnitGoalList;
  onPress: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isMobile?: boolean;
  isOrgEmployee?: boolean;
  isViewOnly?: boolean;
  colors?: {
    card: string;
    cardBorder: string;
    textPrimary: string;
    textMuted: string;
    iconDefault: string;
    border: string;
    backgroundAlt: string;
  };
}

const GoalCard = ({ goal, onPress, onEdit, onDelete, isMobile, isOrgEmployee, isViewOnly, colors }: GoalCardProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const { t } = useLanguage();
  const { translate, translateUnit } = useAutoTranslate();
  const isPending = goal.approval_status === 'pending';

  // Default colors for backwards compatibility
  const cardBg = colors?.card || tokens.colors.raw.white;
  const cardBorderColor = colors?.cardBorder || tokens.colors.raw.slate["200"];
  const textPrimary = colors?.textPrimary || tokens.colors.semantic.general.foreground;
  const textMuted = colors?.textMuted || tokens.colors.raw.gray["500"];
  const iconColor = colors?.iconDefault || tokens.colors.semantic.general.foreground;
  const trackColor = colors?.border || tokens.colors.raw.gray["200"];
  const dropdownBg = colors?.card || tokens.colors.raw.white;
  const dropdownBorder = colors?.cardBorder || tokens.colors.raw.slate["200"];

  return (
    <View style={[styles.goalCard, isMobile && styles.goalCardMobile, { backgroundColor: cardBg, borderColor: cardBorderColor }]}>
      {/* Card Content - RTL: info on right, progress on left visually */}
      <View style={styles.goalCardContent}>
        {/* Circular Progress - appears on left in RTL (put first so it's on left) */}
        <View style={styles.goalCardProgressContainer}>
          <CircularProgress percentage={goal.commitment_rate ?? null} trackColor={trackColor} textColor={textPrimary} />
        </View>

        {/* Goal Info - appears on right in RTL */}
        <View style={styles.goalCardInfo}>
          {/* Title */}
          <Text style={[styles.goalCardTitle, { color: textPrimary }]} numberOfLines={2}>
            {translate(goal.name)}
          </Text>

          {/* Pending Badge */}
          {isPending && (
            <View style={styles.pendingBadge}>
              <Text style={styles.pendingBadgeText}>{t('unitGoals.pendingApproval')}</Text>
            </View>
          )}
          {/* Pending Deletion Badge */}
          {goal.has_pending_deletion && <PendingDeletionBadge compact />}

          {/* Unit Badge - Gold background */}
          <View style={styles.goalCardBadge}>
            <Text style={styles.goalCardBadgeText} numberOfLines={1}>
              {translateUnit(goal.unit_name)}
            </Text>
          </View>

          {/* Description */}
          <Text style={[styles.goalCardDescription, { color: textMuted }]} numberOfLines={2}>
            {translate(goal.description) || translate(goal.strategic_goal_title) || t('common.noDescription')}
          </Text>
        </View>
      </View>

      {/* Card Footer */}
      <View style={styles.goalCardFooter}>
        {/* View Details Link - appears on right in RTL */}
        <TouchableOpacity style={styles.goalCardDetailsLink} onPress={onPress}>
          <ChevronLeftIcon size={16} color={iconColor} />
          <Text style={[styles.goalCardDetailsText, { color: textMuted }]}>{t('common.viewDetails')}</Text>
        </TouchableOpacity>

        {/* More Menu - appears on left in RTL - Hidden for view-only users (org_executive, executive, strategy_team) */}
        {!isViewOnly && (
          <View style={styles.menuContainer}>
            <TouchableOpacity
              style={styles.goalCardMenuButton}
              onPress={() => setShowMenu(!showMenu)}
            >
              <MoreVerticalIcon size={20} color={iconColor} />
            </TouchableOpacity>

            {/* Dropdown Menu */}
            {showMenu && (
            <View style={[styles.menuDropdown, { backgroundColor: dropdownBg, borderColor: dropdownBorder }]}>
              <TouchableOpacity
                style={[styles.menuItem]}
                onPress={() => {
                  setShowMenu(false);
                  onEdit();
                }}
              >
                <Text style={[styles.menuItemText, { color: textPrimary }]}>{t('common.edit')}</Text>
                <EditIcon size={16} color={iconColor} />
              </TouchableOpacity>
              {/* Delete option with approval workflow for org_employee */}
              <TouchableOpacity
                style={[styles.menuItem, styles.menuItemLast]}
                onPress={() => {
                  setShowMenu(false);
                  onDelete();
                }}
              >
                <Text style={[styles.menuItemText, styles.menuItemTextDanger]}>
                  {t('common.delete')}
                </Text>
                <TrashIcon size={16} color={tokens.colors.raw.red["500"]} />
              </TouchableOpacity>
            </View>
          )}
          </View>
        )}
      </View>
    </View>
  );
};

// ============================================================
// Main Screen
// ============================================================
type RouteProps = RouteProp<MainStackParamList, 'UnitGoals'>;

export default function UnitGoalsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const organizationId = route.params?.organizationId;
  const organizationName = route.params?.organizationName;
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { isMobile, isTablet, isDesktop, containerPadding } = useResponsive();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { selectedYear } = useYear();
  const { translateUnit } = useAutoTranslate();

  // Theme colors object to pass to cards
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["100"],
    sectionBg: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"],
  };

  const [refreshing, setRefreshing] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<number | null>(null);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const { ref: unitTriggerRef, pos: unitDropdownPos, measure: measureUnitTrigger } = useDropdownAnchor();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Use responsive values from hook
  const isSmallScreen = isMobile;
  const isMediumScreen = isTablet;
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';

  // Evidence role flags
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const canViewAllEvidence = useCanViewAllOrgs();
  const ownDeptId = user?.profile?.organization;

  const queryClient = useQueryClient();

  // Main data query — React Query renders cached data instantly on focus
  // and refetches in the background, so there's no reload spinner on
  // tab switches / back-navigation.
  const {
    data: goalsData,
    isLoading: loading,
    refetch,
  } = useUnitGoalsQuery({
    page: currentPage,
    pageSize: 10,
    department_id: organizationId,
    unit: selectedUnit ?? undefined,
    year: selectedYear,
    // Executive (view-only) users should only see pending unit goals for approval
    approval_status: isViewOnly ? 'pending' : undefined,
  });

  const goals: UnitGoalList[] = useMemo(() => goalsData?.results || [], [goalsData]);
  const totalCount = goalsData?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / 10));

  // Units dropdown list — fetched once, cached for 10 min (rarely changes).
  const { data: unitsData } = useQuery({
    queryKey: ['organizationalUnits', 'list', organizationId],
    queryFn: async () => {
      const unitsParams: Record<string, any> = { page_size: 100 };
      if (organizationId) unitsParams.department_id = organizationId;
      return organizationalUnitsApi.list(unitsParams);
    },
    staleTime: 10 * 60 * 1000,
  });
  const units: OrganizationalUnitList[] = unitsData?.results || [];

  // Refresh on focus — cached data renders instantly, refetch runs silently.
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  // Reset to page 1 when year changes; the query key change triggers a refetch.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  // Back-compat for handlers that previously called fetchData(): invalidate
  // the unit goals query and React Query will refetch on next access.
  const fetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.unitGoals.all });
  }, [queryClient]);

  const handleUnitSelect = (unitId: number | null) => {
    setSelectedUnit(unitId);
    setCurrentPage(1);
    setShowUnitDropdown(false);
  };

  const handleApplyFilter = () => {
    setCurrentPage(1);
    fetchData();
  };

  const handleClearFilter = () => {
    setSelectedUnit(null);
    setCurrentPage(1);
  };

  const getSelectedUnitName = () => {
    if (!selectedUnit) return t('filters.allOrganizationalUnits');
    const unit = units.find(u => u.id === selectedUnit);
    return translateUnit(unit?.name_ar || unit?.name) || t('filters.allOrganizationalUnits');
  };

  const handleEdit = (goalId: number) => {
    navigation.navigate('AddUnitGoal', { unitGoalId: goalId });
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      showError(t('common.noDataToExport'));
      return;
    }

    setIsExporting(true);
    try {
      // Backend builds the report (unit goals + KPI sheet), same filters.
      const params: Record<string, any> = { lang: language };
      if (organizationId) params.department_id = organizationId;
      if (selectedUnit) params.unit = selectedUnit;

      const response = await unitGoalsApi.exportExcel(params);
      downloadBlobAsFile(response.data, `${t('exportFiles.unitGoals')}.xlsx`);
      showSuccess(t('messages.exportSuccess'));
    } catch (error) {
      console.error('Unit goals export error:', error);
      showError(t('messages.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Results export - same filters as handleExportExcel, results workbook + year-scoped filename
  const handleExportResults = async () => {
    if (totalCount === 0) {
      showError(t('common.noDataToExport'));
      return;
    }

    setIsExporting(true);
    try {
      const params: Record<string, any> = { lang: language };
      if (organizationId) params.department_id = organizationId;
      if (selectedUnit) params.unit = selectedUnit;

      const response = await unitGoalsApi.resultsExport(params);
      downloadBlobAsFile(response.data, `${t('exportFiles.unitGoalsResults')}_${selectedYear}.xlsx`);
      showSuccess(t('messages.exportSuccess'));
    } catch (error) {
      console.error('Unit goals export error:', error);
      showError(t('messages.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteConfirm = (goalId: number) => {
    setSelectedGoalId(goalId);
    setShowDeletionImpact(true);
  };

  const handleDelete = async () => {
    if (!selectedGoalId) return;

    try {
      setDeleting(true);
      const response = await unitGoalsApi.delete(selectedGoalId);

      if (response?.status === 202 || response?.data?.status === 'pending') {
        showSuccess(t('messages.deleteRequestSent'));
      } else {
        showSuccess(t('unitGoals.deleteSuccess'));
      }

      fetchData();
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('messages.deleteRequestSent'));
        fetchData();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('unitGoals.deleteFailed'));
      }
    } finally {
      setDeleting(false);
      setShowDeletionImpact(false);
      setSelectedGoalId(null);
    }
  };

  // Full-page skeleton only on cold load (no cached data yet). Warm cache
  // and background refetches fall through to the regular render.
  if (loading && goals.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <GoalsSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, { padding: containerPadding }]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={organizationId ? [
            { label: t('navigation.home') },
            { label: t('nav.organizations'), route: 'Organizations' },
            { label: organizationName || '', route: 'OrganizationDetail', params: { id: organizationId } },
            { label: t('unitGoals.title') },
          ] : [
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('unitGoals.title') },
          ]}
        />

        <PageHeader
          title={t('unitGoals.title')}
          actions={
            <View style={styles.headerActions}>
              {!isViewOnly && (
                <>
                  <Button
                    variant="primary"
                    onPress={() => navigation.navigate('AddUnitGoal', {})}
                    trailingIcon={<PlusIcon size={16} color={tokens.colors.raw.white} />}
                  >
                    {t('unitGoals.addNewGoal')}
                  </Button>
                  {isOrgEmployee && (
                    <Button
                      variant="secondary"
                      onPress={() => navigation.navigate('ImportUnitGoals')}
                      trailingIcon={<FileInputIcon size={16} color={themeColors.textPrimary} />}
                    >
                      {t('unitGoals.importExcel')}
                    </Button>
                  )}
                </>
              )}
              <ExportMenuButton
                onExportData={handleExportExcel}
                onExportResults={handleExportResults}
                isExporting={isExporting}
              />
            </View>
          }
          contentTrailing={
          <View style={[styles.filterSection, isSmallScreen && styles.filterSectionMobile]}>
            {/* Clear Filter Button - Only shown when filter is active */}
            {selectedUnit && (
              <Button
                variant="destructive"
                onPress={handleClearFilter}
                leadingIcon={<XIcon size={14} color={tokens.colors.raw.white} />}
                style={{ flex: isSmallScreen ? 1 : undefined }}
              >
                {t('filters.clearFilter')}
              </Button>
            )}

            {/* Apply Filter Button */}
            <Button variant="secondary" onPress={handleApplyFilter}>
              {t('common.applyFilter')}
            </Button>

            {/* Unit Dropdown */}
            <View style={styles.filterDropdownContainer}>
              <View ref={unitTriggerRef as any} collapsable={false}>
                <TouchableOpacity
                  style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                  onPress={() => { if (!showUnitDropdown) measureUnitTrigger(); setShowUnitDropdown(!showUnitDropdown); }}
                >
                  <ChevronDownIcon size={16} color={themeColors.textMuted} />
                  <Text style={[styles.filterDropdownText, { color: themeColors.textPrimary }]} numberOfLines={1}>
                    {getSelectedUnitName()}
                  </Text>
                </TouchableOpacity>
              </View>

              <DropdownPortal
                open={showUnitDropdown}
                onClose={() => setShowUnitDropdown(false)}
                pos={unitDropdownPos}
                backgroundColor={themeColors.card}
                borderColor={themeColors.cardBorder}
                maxHeight={300}
              >
                <ScrollView style={styles.dropdownScroll} nestedScrollEnabled>
                  <TouchableOpacity
                    style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]}
                    onPress={() => handleUnitSelect(null)}
                  >
                    <Text style={[
                      styles.dropdownItemText,
                      { color: themeColors.textPrimary },
                      !selectedUnit && styles.dropdownItemTextActive
                    ]}>
                      {t('filters.allOrganizationalUnits')}
                    </Text>
                  </TouchableOpacity>
                  {units.map((unit) => (
                    <TouchableOpacity
                      key={unit.id}
                      style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]}
                      onPress={() => handleUnitSelect(unit.id)}
                    >
                      <Text style={[
                        styles.dropdownItemText,
                        { color: themeColors.textPrimary },
                        selectedUnit === unit.id && styles.dropdownItemTextActive
                      ]}>
                        {translateUnit(unit.name_ar || unit.name)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </DropdownPortal>
            </View>
          </View>
          }
          style={{ marginBottom: tokens.spacing.absolute["6"] }}
        />

        {/* Goals Grid */}
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={COLORS.primary} />
          </View>
        ) : goals.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>{t('unitGoals.noUnitGoals')}</Text>
          </View>
        ) : (
          <View style={[
            styles.goalsGrid,
            isSmallScreen && styles.goalsGridMobile,
            isMediumScreen && styles.goalsGridTablet,
          ]}>
            {goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                isMobile={isSmallScreen}
                isOrgEmployee={isOrgEmployee}
                isViewOnly={isViewOnly}
                onPress={() => navigation.navigate('UnitGoalDetail', { id: goal.id })}
                onEdit={() => handleEdit(goal.id)}
                onDelete={() => handleDeleteConfirm(goal.id)}
                colors={themeColors}
              />
            ))}
          </View>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <View style={[styles.pagination, { flexDirection: rtlRow }]}>
            <TouchableOpacity
              style={[styles.paginationButton, currentPage <= 1 && styles.paginationButtonDisabled]}
              onPress={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <Text style={[styles.paginationButtonText, { color: themeColors.textMuted }, currentPage <= 1 && styles.paginationButtonTextDisabled]}>
                {t('pagination.previous')}
              </Text>
              <ChevronRightIcon size={14} color={currentPage <= 1 ? themeColors.border : themeColors.textMuted} />
            </TouchableOpacity>

            <View style={[styles.paginationNumbers, { flexDirection: rtlRow }]}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <TouchableOpacity
                    key={pageNum}
                    style={[
                      styles.paginationNumber,
                      { backgroundColor: themeColors.sectionBg },
                      currentPage === pageNum && styles.paginationNumberActive,
                    ]}
                    onPress={() => setCurrentPage(pageNum)}
                  >
                    <Text style={[
                      styles.paginationNumberText,
                      { color: themeColors.textMuted },
                      currentPage === pageNum && styles.paginationNumberTextActive,
                    ]}>
                      {pageNum}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <TouchableOpacity
              style={[styles.paginationButton, currentPage >= totalPages && styles.paginationButtonDisabled]}
              onPress={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              <ChevronLeftIcon size={14} color={currentPage >= totalPages ? themeColors.border : themeColors.textMuted} />
              <Text style={[styles.paginationButtonText, { color: themeColors.textMuted }, currentPage >= totalPages && styles.paginationButtonTextDisabled]}>
                {t('pagination.next')}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        <EvidenceTable
          relatedType="unit_goal_collection"
          filterRelatedId={canViewAllEvidence ? undefined : (ownDeptId ?? undefined)}
          title={t('evidence.sectionTitle')}
          canManage={canManageEvidence && !!ownDeptId}
          showOrgColumn={canViewAllEvidence}
          onAddPress={() => navigation.navigate('AddEvidence', {
            relatedType: 'unit_goal_collection',
            relatedId: ownDeptId as number,
            contextLabel: t('unitGoals.title'),
          })}
          onEditPress={(ev) => navigation.navigate('AddEvidence', {
            evidenceId: ev.id,
            relatedType: ev.related_type,
            relatedId: ev.related_id,
            contextLabel: t('unitGoals.title'),
          })}
        />
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => { setShowDeletionImpact(false); setSelectedGoalId(null); }}
        onConfirm={handleDelete}
        entityName={goals.find(g => g.id === selectedGoalId)?.name || ''}
        fetchImpact={async () => {
          const response = await unitGoalsApi.deletionImpact(selectedGoalId!);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={deleting}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: tokens.spacing.absolute["10"],
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  breadcrumbText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["400"],
  },
  breadcrumbActive: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: COLORS.primary,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["5"],
  },
  filterSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["4"],
    zIndex: 1000,
  },
  filterSectionMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  filterDropdownContainer: {
    position: 'relative',
    minWidth: 250,
    zIndex: 9999,
  },
  dropdownOverlay: {
    position: 'fixed' as any,
    top: 0,
    start: 0,
    end: 0,
    bottom: 0,
    zIndex: 9998,
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.white,
    minHeight: 44,
  },
  filterDropdownText: {
    flex: 1,
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["700"],
    textAlign: getTextAlign(isRTL),
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    start: 0,
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginTop: tokens.spacing.absolute["1"],
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 50,
    zIndex: 9999,
    overflow: 'hidden',
  },
  dropdownScroll: {
    maxHeight: 250,
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
    minHeight: 44,
    justifyContent: 'center',
  },
  dropdownItemText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["700"],
    textAlign: getTextAlign(isRTL),
  },
  dropdownItemTextActive: {
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.raw.gray["400"],
  },
  goalsGrid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["4"],
    justifyContent: 'flex-start',
  },
  goalsGridMobile: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  goalCardMobile: {
    flexBasis: '100%',
    width: '100%',
    minWidth: 'auto',
  },
  goalsGridTablet: {
    justifyContent: 'flex-start',
  },
  goalCard: {
    flexBasis: '48%',
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 280,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["4"],
    // Shadow matching Figma
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'hidden',
  },
  goalCardContent: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["6"],
  },
  goalCardProgressContainer: {
    width: 80,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  progressTextContainer: {
    position: 'absolute',
    top: 0,
    start: 0,
    end: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressText: {
    fontFamily: FONTS.numbers,
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: tokens.colors.semantic.general.foreground,
    fontWeight: '700',
  },
  progressDotContainer: {
    position: 'absolute',
    top: -1,
    end: 34,
  },
  progressDot: {
    width: 12,
    height: 12,
    borderRadius: tokens.radii.absolute["radius-6"],
    backgroundColor: '#2CA02C',
    borderWidth: 2,
    borderColor: '#FCFCFC',
  },
  goalCardInfo: {
    flex: 1,
    alignItems: 'flex-end',
    gap: tokens.spacing.absolute["3"],
  },
  goalCardTitle: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  goalCardBadge: {
    backgroundColor: '#A6813F',
    borderRadius: 40,
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  goalCardBadgeText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.white,
  },
  pendingBadge: {
    backgroundColor: '#9467BD',
    borderRadius: 40,
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  pendingBadgeText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.raw.white,
  },
  goalCardDescription: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 14,
  },
  goalCardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  goalCardMenuButton: {
    padding: tokens.spacing.absolute["2_5"],
    minWidth: 44,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  goalCardDetailsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-4"],
    minHeight: 44,
  },
  goalCardDetailsText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: tokens.colors.semantic.general.foreground,
  },
  pagination: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["8"],
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    minHeight: 44,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
  },
  paginationButtonTextDisabled: {
    color: tokens.colors.raw.gray["300"],
  },
  paginationNumbers: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
  },
  paginationNumber: {
    width: 44,
    height: 44,
    borderRadius: tokens.radii.absolute["radius-6"],
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.gray["100"],
  },
  paginationNumberActive: {
    backgroundColor: tokens.colors.raw.gray["800"],
  },
  paginationNumberText: {
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
  },
  paginationNumberTextActive: {
    color: tokens.colors.raw.white,
  },
  // Menu styles
  menuContainer: {
    position: 'relative',
  },
  menuDropdown: {
    position: 'absolute',
    bottom: '100%',
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 8,
    minWidth: 140,
    marginBottom: tokens.spacing.absolute["1"],
    zIndex: 1000,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
    minHeight: 44,
  },
  menuItemLast: {
    borderBottomWidth: 0,
  },
  menuItemText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["700"],
  },
  menuItemTextDanger: {
    color: tokens.colors.raw.red["500"],
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    color: tokens.colors.raw.gray["800"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
    fontWeight: '600',
  },
  modalMessage: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.gray["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
  },
  modalCancelButtonText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
  },
  modalDeleteButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.red["500"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
  },
  modalDeleteButtonText: {
    fontFamily: FONTS.medium,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.white,
  },
});

const styles = getStyles(true);
